import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEnquiry } from "@/context/EnquiryContext";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { COMPANY, IMAGES } from "@/data/content";

const LINKS = [
  { label: "About", to: "/#about" },
  { label: "Products", to: "/#products" },
  { label: "Gallery", to: "/#gallery" },
  { label: "Blog", to: "/blog" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => (e) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 500);
      } else {
        scrollToId(id);
      }
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding,box-shadow] duration-300 bg-brand-cream/90 backdrop-blur-xl border-b border-brand-green/15 ${
        scrolled ? "py-2.5 shadow-[0_8px_30px_rgba(22,101,52,0.12)]" : "py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center gap-3 group" onClick={() => scrollToTop()}>
          <motion.span
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="relative shrink-0"
          >
            <span className="absolute -inset-1.5 rounded-2xl bg-brand-green/25 blur-md animate-pulse-ring" aria-hidden="true" />
            <img
              src={IMAGES.logo}
              alt="ADK SOLUTIONS logo"
              className="relative h-14 sm:h-16 w-auto rounded-xl border-2 border-brand-green/30 bg-brand-cream ring-2 ring-brand-green/15 shadow-[0_6px_28px_rgba(22,101,52,0.35)] group-hover:scale-105 group-hover:shadow-[0_8px_36px_rgba(22,101,52,0.5)] transition-transform duration-300"
            />
          </motion.span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-extrabold tracking-tight text-xl sm:text-2xl bg-brand-green text-brand-cream px-2.5 py-0.5 rounded-md shadow-sm">ADK SOLUTIONS</span>
            <span className="mt-1 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-brand-brown font-bold">Powering Modern Agriculture</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) =>
            l.to.startsWith("/#") ? (
              <a key={l.label} href={l.to} onClick={go(l.to)} data-testid={`nav-${l.label.toLowerCase()}`} className="text-sm font-bold text-brand-greenDark hover:text-brand-brown transition-colors">
                {l.label}
              </a>
            ) : (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="text-sm font-bold text-brand-greenDark hover:text-brand-brown transition-colors">
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={`tel:${COMPANY.phoneRaw}`} data-testid="nav-call" className="flex items-center gap-2 text-sm font-bold text-brand-green hover:text-brand-greenDark transition-colors">
            <span className="grid place-items-center size-8 rounded-full bg-brand-green text-white shadow-md animate-pulse-ring">
              <Phone className="size-4" />
            </span>
            {COMPANY.phone}
          </a>
          <Button data-testid="nav-quote" onClick={() => openEnquiry("", "enquiry")} className="rounded-full bg-brand-brown text-white hover:bg-brand-green px-5 shadow-md">Request a Quote</Button>
        </div>

        <button data-testid="menu-toggle" className="lg:hidden text-brand-green" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl bg-brand-cream border border-brand-ink/10 p-5 shadow-xl" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) =>
              l.to.startsWith("/#") ? (
                <a key={l.label} href={l.to} onClick={go(l.to)} className="text-base font-medium text-brand-ink">{l.label}</a>
              ) : (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-base font-medium text-brand-ink">{l.label}</Link>
              )
            )}
            <Button onClick={() => { setOpen(false); openEnquiry("", "enquiry"); }} className="rounded-full bg-brand-green text-white hover:bg-brand-greenDark mt-1">Request a Quote</Button>
          </div>
        </div>
      )}
    </header>
  );
}
